import { useEffect, useMemo } from 'react'

import { useLoaderData, useLocation } from 'react-router'

import { ProductContentLoaderData, loader as contentLoader } from './loader'
import ApiViewer from '~/components/ApiViewer'
import { ErrorBoundaryComponent } from '~/components/ErrorBoundary'
import { SyntaxRenderer } from '~/components/SyntaxRenderer'
import { useConsumerBranding } from '~/hooks/useConsumerBranding'
import { useProducts } from '~/hooks/useProducts'
import { AsyncAPIDefinition, OpenAPI2Definition, OpenAPI3Definition } from '~/types/api.definitions'
import { CONTENT_TYPE } from '~/types/content'
import {
  getProviderContentSlug,
  getProviderTemplateContentSlug,
  getTOCBreadcrumbPaths,
  isPublishedApiContent,
  isPublishedDocumentContent,
  isPublishedGraphQlContent
} from '~/utils/shared/content'
import { formatDate } from '~/utils/shared/dateTime'
import { consumerMeta } from '~/utils/shared/meta'
import { isAtLeastDesigner } from '~/utils/shared/roles'

export const handle = {
  nestedBreadcrumbs: ({ match, matches }) => {
    const toc = matches.find(match => match.id === 'routes/_docs.$productname/_index')?.data?.sections?.items?.[0]?.tableOfContents
    return getTOCBreadcrumbPaths(toc, match.pathname)
  }
}

export const loader = contentLoader

export const meta = consumerMeta

export default function DocsProductDocumentationRootPage() {
  const {
    currentToc,
    type,
    content,
    contentUpdatedAt,
    path,
    generatorLanguages,
    exploreUrl,
    shubUrl,
    SHUBApiDetails = {},
    apiRenderer,
    routingSettings,
    isTemplatePreview,
  } = useLoaderData<ProductContentLoaderData>()

  const { accentColor, fontName } = useConsumerBranding()

  const { currentProduct, currentProductRole } = useProducts()
  const showEditButtons = isAtLeastDesigner(currentProductRole)

  const lastModifiedDate = useMemo(() => formatDate(contentUpdatedAt), [contentUpdatedAt])
  const providerContentSlug = isTemplatePreview && currentToc && 'sourceTemplateTocId' in currentToc
    ? getProviderTemplateContentSlug({ templateId: currentProduct?.id, contentId: currentToc.sourceTemplateTocId })
    : getProviderContentSlug({ productSlug: currentProduct?.slug, contentId: currentToc?.tableOfContentsId })

  const location = useLocation()

  const isMarkdown = type === CONTENT_TYPE.MARKDOWN

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1))
      if (element) {
        element.scrollIntoView()
      }
    }
  }, [location])

  return (
    (type && type !== 'invalid') &&
    <div className="h-full">
      {isPublishedDocumentContent(currentToc) ? (
        <div
          data-testid={`${isMarkdown ? 'markdown-container' : 'html-container'}`}
          className="relative h-full"
          style={{ fontFamily: fontName }}
        >
          <SyntaxRenderer
            type={isMarkdown ? CONTENT_TYPE.MARKDOWN : CONTENT_TYPE.HTML}
            content={content}
            title={currentToc?.title}
            lastModifiedDate={lastModifiedDate}
            providerContentSlug={providerContentSlug}
            showEditButtons={showEditButtons}
          />
        </div>
      ) : null}
      {isPublishedGraphQlContent(currentToc) ? (
        <div
          data-testid="graphql-markdown-container"
          className="relative h-full"
          style={{ fontFamily: fontName }}
        >
          <SyntaxRenderer
            type={CONTENT_TYPE.GRAPHQL}
            content={content}
            title={currentToc?.title}
            lastModifiedDate={lastModifiedDate}
            providerContentSlug={providerContentSlug}
            showEditButtons={showEditButtons}
            url={currentToc.url}
          />
        </div>
      ) : null}
      {isPublishedApiContent(currentToc) ? (
        <ApiViewer
          title={currentToc?.title}
          definition={content as OpenAPI2Definition | OpenAPI3Definition | AsyncAPIDefinition}
          generatorLanguages={generatorLanguages}
          productId={currentProduct?.id}
          path={path}
          exploreUrl={exploreUrl}
          shubUrl={shubUrl}
          SHUBApiDetails={SHUBApiDetails}
          showEditButtons={showEditButtons}
          renderer={apiRenderer}
          lastModifiedDate={lastModifiedDate}
          accentColor={accentColor}
          routingSettings={routingSettings}
          providerContentSlug={providerContentSlug}
          isConsumerSide={true}
        />
      ) : null}
    </div>

  )
}

export const ErrorBoundary = ErrorBoundaryComponent